import type { CSSProperties } from 'react';
import { useState } from 'react';

import type {
  Classnames,
  Maybe,
  RenderFn,
  TypoColor,
  TypoSize,
  TypoWeight,
} from '@snowit/icetypes';

import ButtonIcon from '@snowit/ui/Buttons/ButtonIcon';
import Typo from '@snowit/ui/Typography/Typo';
import copyToClipboard from '@snowit/utils/copyToClipboard';

import cx from 'classnames';
import s from './TinyTableV2.module.scss';

export interface TinyTableV2Data {
  key: string;
  label?: string;
  value?: RenderFn;
  onClick?: () => void;
  copyValue?: Maybe<string>;
  span?: number;
  fullSpan?: boolean;
  hidden?: boolean;
}

export type TinyTableItem = {
  size?: TypoSize;
  weight?: TypoWeight;
  color?: TypoColor;
  width?: string | number;
};

export type TinyTableV2Props = {
  data: TinyTableV2Data[];
  display?: 'grid' | 'flex';
  flow?: 'row' | 'column';
  columns?: number;
  gap?: number | [number, number];
  innerGap?: number | [number, number];
  label?: TinyTableItem;
  value?: TinyTableItem;
  labelOnTop?: boolean;
  classNames?: Classnames<'wrapper' | 'inner' | 'label' | 'wrapValue' | 'value' | 'copyButton'>;
};

const TinyTableV2 = (props: TinyTableV2Props) => {
  const {
    display = 'flex',
    columns = 1,
    flow = 'row',
    gap = 0.5,
    innerGap = 0.5,
    labelOnTop = false,
    classNames,
  } = props;

  const label: TinyTableItem = {
    width: 8,
    size: 'df',
    weight: 'regular',
    color: 'text-l',
    ...props.label,
  };
  const value: TinyTableItem = {
    width: '1fr',
    size: 'df',
    weight: 'regular',
    color: 'text',
    ...props.value,
  };

  const [copied, setCopied] = useState<string>('');

  const data = props.data.filter((item) => !item.hidden);
  const length = data.reduce((memo, item) => {
    const span = item.fullSpan ? columns : Math.min(columns, item.span || 1);
    return memo + span;
  }, 0);

  const rows = Math.ceil(length / columns);

  const gaps: [number, number] = Array.isArray(gap) ? gap : [gap, gap];
  const innerGaps: [number, number] = Array.isArray(innerGap) ? innerGap : [innerGap, innerGap];

  const [gapRow, gapCol] = gaps;
  const [innerGapRow, innerGapCol] = innerGaps;

  const handleCopy = (item: TinyTableV2Data): void => {
    copyToClipboard(item.copyValue || '', () => {
      setCopied(item.key);
      setTimeout(() => {
        setCopied('');
      }, 2000);
    });
  };

  const renderCopy = (item: TinyTableV2Data) => {
    if (!item.copyValue) return null;
    return (
      <ButtonIcon
        classNames={{ button: classNames?.copyButton }}
        icon={copied === item.key ? 'check_circle' : 'copy'}
        onClick={({ event }) => {
          event.stopPropagation();
          handleCopy(item);
        }}
        theme={'ghost'}
        size={'xxl'}
        color={copied === item.key ? 'ok' : 'text'}
      />
    );
  };

  const renderLabel = (itemLabel?: string) => {
    if (!itemLabel) return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={itemLabel}
        tag={'p'}
        size={label.size}
        weight={label.weight}
        color={label.color}
      />
    );
  };

  const renderValue = (itemValue: RenderFn) => {
    const realValue = typeof itemValue === 'function' ? itemValue() : itemValue;
    if (!realValue) return null;

    if (typeof realValue === 'string' || typeof realValue === 'number') {
      return (
        <Typo
          className={cx(s.value, classNames?.value)}
          text={realValue}
          tag={'p'}
          size={value.size}
          weight={value.weight}
          color={value.color}
        />
      );
    }

    return realValue;
  };

  const renderContentValue = (item: TinyTableV2Data) => {
    return (
      <div className={cx(s.wrapValue, classNames?.wrapValue)} onClick={item.onClick}>
        {renderValue(item.value)}
        {renderCopy(item)}
      </div>
    );
  };

  const renderInner = (item: TinyTableV2Data) => {
    const span = item.fullSpan ? columns : Math.min(columns, item.span || 1);
    const labelWidth = typeof label.width === 'number' ? `${label.width}rem` : label.width;
    const valueWidth = typeof value.width === 'number' ? `${value.width}rem` : value.width;
    return (
      <div
        key={item.key}
        className={cx(s.inner, classNames?.inner, {
          [s.copy]: item.copyValue,
          [s.link]: Boolean(item.onClick),
        })}
        style={
          {
            '--l-col-inner-label': labelWidth,
            '--l-col-inner-value': valueWidth,
            '--l-row-inner-gap': `${innerGapRow}rem`,
            '--l-col-inner-gap': `${innerGapCol}rem`,
            '--l-span-inner': span,
          } as CSSProperties
        }
        onClick={item.onClick}
      >
        {renderLabel(item.label)}
        {renderContentValue(item)}
      </div>
    );
  };

  return (
    <div
      className={cx(s.wrapper, classNames?.wrapper, {
        [s.labelOnTop]: labelOnTop,
      })}
      style={
        {
          '--l-display': display,
          '--l-columns': columns > 1 ? columns : undefined,
          '--l-rows': rows,
          '--l-flow': flow,
          '--l-row-gap': `${gapRow}rem`,
          '--l-col-gap': `${gapCol}rem`,
        } as CSSProperties
      }
    >
      {data.map((item) => renderInner(item))}
    </div>
  );
};

export default TinyTableV2;
